import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from './styled';
import { useDrag } from 'react-dnd';
import { Mixpanel } from 'helpers/Mixpanel';
import './Cell.scss';

import presenter from 'pages/ScheduleCalendar/duck/presenter';
import service from 'pages/ScheduleCalendar/duck/service';
import { isValidObject } from 'helpers/validation';
import { getShiftDetails } from '../shared/cellUtils';
import { getRoleTagColor, findFirstRoleTag } from 'pages/ScheduleCalendar/duck/tagUtil';
import constants from 'pages/ScheduleCalendar/constants';
import text from 'strings/text';
import draftLogic from 'pages/ScheduleCalendar/duck/draftLogic';

export function DraggableShiftCell({
  user,
  shifts,
  selectedDateToAddShift,
  date,
  handleOpenShiftDetail,
  copyShiftToUserAndDateDispatch,
  shiftToCopy,
  isCopyingShift,
  allUsers,
  allUserShiftsOnDay,
  handleTooltipHide,
  handleTooltipShow,
  addStep,
  roles,
}) {

  const ref = useRef(null);

  useEffect(() => {
    const selector = "[data-id='Deefeature27']";
  
    const waitForElement = () => {
      const el = document.querySelector(selector);
  
      if (el) {
        addStep?.({
          target: selector,
          title: "Create Your First Shift",
          content: "Assign shifts to team members, apply them to multiple days, or save as a template. Then copy & paste or drag & drop shifts to streamline your scheduling process.",
          placement: "right",
        });
  
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        setTimeout(waitForElement, 300); // Try again
      }
    };
  
    waitForElement();
  }, [addStep]);
  

  const canDragShift = useCallback(() => {
    if (shifts != null) {
      const shiftToDrag = shifts[0];
      if (shiftToDrag != null) {
        if (shiftToDrag.trade_status === 'unpublished_inventory' || shiftToDrag.trade_status === 'unregistered_unpublished_inventory') {
          return true;
        } else {
          return false;
        }
      }
    }
    return false;
  }, [shifts]);

  const [{ isDragging, canDrag }, drag] = useDrag(() => ({
    type: 'ShiftCell',
    item: () => (shifts?.length ? shifts[0] : { id: 0 }),
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: canDragShift(),
  }), [canDragShift, shifts]);

  drag(ref);

  const [isMouseOverCell, setIsMouseOverCell] = useState(false);

  const onCellClick = useCallback(() => {
    if (shifts && shifts.length > 0) {
      Mixpanel.track('View shift detail', { 'target user': user.owner_id, 'date': date.format('YYYY-MM-DD') });
      handleOpenShiftDetail(shifts[0], date);
    } else {
      setIsMouseOverCell(false);
      selectedDateToAddShift(user, date, constants.ShiftCreationType.Inventory);

      // Add Shifts
      if (user.status !== 'unregistered') {
        Mixpanel.track('Add shift from cell', { 'target user': user.owner_id, 'date': date.format('YYYY-MM-DD') });
      } else {
        Mixpanel.track('Clicked unregistered user cell', { 'target user': user.id, 'date': date.format('YYYY-MM-DD') });
      }
    }
  }, [shifts, user, date, handleOpenShiftDetail, selectedDateToAddShift]);

  const onPasteClick = useCallback(() => {
    setIsMouseOverCell(false);
    copyShiftToUserAndDateDispatch(shiftToCopy, user, date, shiftToCopy.location_id);
    Mixpanel.track('Paste Shift');
  }, [copyShiftToUserAndDateDispatch, shiftToCopy, user, date]);

  const onEmptyCellMouseEnter = () => { setIsMouseOverCell(true); };

  const onEmptyCellMouseLeave = useCallback(() => { setIsMouseOverCell(false); }, []);

  const onAvatarMouseOver = (e) => {
    if (presenter.shouldShowTakerAvatar(shifts[0]) == true) {
      const content = presenter.xCoveringForYText(user, shifts[0], allUsers);
      handleTooltipShow(e, content);
    }
  };

  const onConflictMouseOver = (e) => {
    const name = user.first_name + ' ' + user.last_name;
    handleTooltipShow(e, 'Conflict: ' + name + ' has another shift on this day.');;
  };

  const onConflictMouseOut = () => {
    handleTooltipHide();
  };


  if (isValidObject(shifts) == false || shifts.length === 0) {
    if (isCopyingShift && shiftToCopy !== null) { 
      if( draftLogic.isVTO(shiftToCopy) == false){
        return presenter.pasteShiftCell(onPasteClick, onEmptyCellMouseEnter, onEmptyCellMouseLeave, isMouseOverCell);
      }else{
        return <br/>
      }
    } else {
      return presenter.addNewShiftCell(onCellClick, onEmptyCellMouseEnter, onEmptyCellMouseLeave, isMouseOverCell);
    }
  }

  const shiftObject = shifts[0];
  const { position, hours, timeRange } = getShiftDetails(shiftObject);
  const posterAvatar = presenter.posterAvatar(shiftObject, allUsers);
  const takerAvatar = presenter.takerAvatar(shiftObject, allUsers);
  
  const showAvatar = (presenter.shouldShowPosterAvatar(shiftObject) || presenter.shouldShowTakerAvatar(shiftObject));
  const textHours = (<FormattedMessage id="DayCell.hours" defaultMessage="{value} h" values={{ value: hours }} />);
  const roleTag = findFirstRoleTag(roles, shiftObject);
  const positionOrRoleName = presenter.getRoleNameOrJobTitle([roleTag], position);

  const isVTO = shiftObject.name === 'shift_vto';

  const bottomLine = (
    <styled.DayCellBottomLineWrap>
      {presenter.shouldShowPosterAvatar(shiftObject) == true && <styled.DayCellAvatar1 src={posterAvatar} onMouseOver={onAvatarMouseOver} onMouseOut={onConflictMouseOut}></styled.DayCellAvatar1>}
      {presenter.shouldShowTakerAvatar(shiftObject) == true && <styled.DayCellAvatar2 src={takerAvatar} onMouseOver={onAvatarMouseOver} onMouseOut={onConflictMouseOut}></styled.DayCellAvatar2>}

      {isVTO && <styled.DayCellTextVTO>{text.VTO()}</styled.DayCellTextVTO> }
      {isVTO && <styled.Dot />}

      <label className={`position-hours ${showAvatar ? 'left-margin-4' : ''}`}>{positionOrRoleName}</label>
      {typeof positionOrRoleName === 'string' && positionOrRoleName.length > 0 && <styled.Dot />}
      <styled.BottomLineText>{textHours}</styled.BottomLineText>
    </styled.DayCellBottomLineWrap>
  );

  var isDraftShift = false;
  if (shifts[0]?.trade_status === 'unpublished_inventory' ||
    shifts[0]?.trade_status === 'unregistered_unpublished_inventory') {
    isDraftShift = true;
  }

  const cardClassName = presenter.cardClassNameForShift(shiftObject);
  const tagText = presenter.tagTextForShiftCard(shiftObject);
  const shouldShowConflict = service.userHasShiftConflict(shifts[0], allUserShiftsOnDay);
  const roleTagColor = getRoleTagColor(roles, shiftObject);

  const backgroundColor = isVTO ? '#ffffff' : presenter.backgroundColorFromRoleTagColor(roleTagColor);

  
  return (
    <div className={"day-cell-top-wrap" + " " + cardClassName} ref={ref} onMouseUpCapture={onCellClick} key={shifts[0].id}>
      <span className='status-tag'> {tagText} </span>

      <div className={(isDraftShift) ? 'day-cell dotted-border' : 'day-cell solid-border'} style={{backgroundColor: backgroundColor, borderColor: presenter.borderColorFromRoleTagColor(roleTagColor)}}>
        <div className='top-row'>
          <label className='time-label'> {timeRange} </label>
          {shouldShowConflict && <img className='conflict-sign' src='/icons/conflict.svg' onMouseOver={onConflictMouseOver} onMouseOut={onConflictMouseOut} alt="conflict"></img>}
        </div>
        {bottomLine}
      </div>

    </div>
  );

};

export default DraggableShiftCell;
